
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import { action, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import {
    Address, AddressPurpose, CountryRegionInfo, StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { AutoSuggest } from '../../common/address/address-autosuggest';
import { IAutoSuggestOptions } from '../../common/address/address-autosuggest.data';
import { AddressCommon } from '../../common/address/address-common';
import { AddressFormat } from '../../common/address/address-format';
import { AddressItemType, IAddressDropdownsData } from '../../common/address/address-format.data';
import { AddressMetaData } from '../../common/address/address-meta-data';
import {
    AddressOperation, AddressType, IAddressResource, IAddressResponse, IAddressConfig
} from '../../common/address/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from '../../common/address/components/address-add';
import { AddressList, IAddressListProps } from '../../common/address/components/address-list';
import AddressSearch, { IFormattedAddressDetails } from '../../components/citta-address-search/citta-address-search';
import { ICittaAccountManagementAddressData } from './citta-account-management-address.data';
import { ICittaAccountManagementAddressProps } from './citta-account-management-address.props.autogenerated';

export interface IAccountManagementAddressViewState {
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}

export interface IAccountManagementAddressViewProps extends React.Component<ICittaAccountManagementAddressProps<ICittaAccountManagementAddressData>> {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: IAccountManagementAddressViewState;
    AccountAddressManagement: IModuleProps;
    isUpdating: boolean;
    showAddressList: IAddressListProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * Address component
 * @extends {React.Component<ICittaAccountManagementAddressProps<ICittaAccountManagementAddressData>>}
 */
@observer
class AccountManagementAddress extends React.Component<ICittaAccountManagementAddressProps<ICittaAccountManagementAddressData>> {
    @observable private currentOperation: AddressOperation;
    @observable private selectedAddress?: Address;
    @observable private countryRegionId: string = 'NZL';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private customerAddresses: Address[];
    @observable private addUpdateAddress: Address;
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private isUpdating?: boolean;
    @observable private hasError?: boolean;
    @observable private manualAddress: boolean;

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private addressPurposes: AddressPurpose[];
    private resources: IAddressResource;
    private config:IAddressConfig;
    private defaultAddressType: number = this.props.context.app.config.defaultAddressTypeValue || 7; // Default to Home
    private telemetryContent: ITelemetryContent;
    private autoSuggest?: AutoSuggest;
    private isNZ: boolean = false;
    private addressDropdownsData:IAddressDropdownsData = {};
    private showAddressSearch: boolean = false;
    private nzlCountryRegionId: string = 'NZL';

    constructor(props: ICittaAccountManagementAddressProps<ICittaAccountManagementAddressData>) {
        super(props);
        const { context, data, resources, telemetry, config } = this.props;

        this.resources = resources;
        this.config = config
        this.currentOperation = AddressOperation.List;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.customerAddresses = data.address.result || [];
        this.addUpdateAddress = {};
        this.validationError = {};
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(this.countryRegions, new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()), this.addressPurposes);
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this.isNZ = this.selectedAddress && this.selectedAddress.ThreeLetterISORegionName === 'NZL' || false;
        this.manualAddress = false;
    }

    public async componentDidMount(): Promise<void> {

        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: {
                        channel
                    }
                }
            },
            config: {
                autoSuggestionEnabled,
                autoSuggestOptions
            }
        } = this.props;

        this.props.data.countryRegions.then(() => {
            this._setDefaultCountryRegionId();
        });

        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );

        if (autoSuggestionEnabled) {

            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }

            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }

            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(telemetry, options, channel?.BingMapsApiKey, channel?.ChannelCountryRegionISOCode, channel?.DefaultLanguageId);

            reaction(
                () =>  this.props.data.storeSelectorStateManager.result?.loadMapApi !== undefined && (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );

            reaction(
                () => {
                    this.props.data.storeSelectorStateManager.result?.isMapApiLoaded;
                },
                async () => {
                    if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
                        await this._attachMapAutoSuggest();
                    }
                }
            );
        }
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            this.autoSuggest?.disposeAutoSuggest();
        }
    }

    public render(): JSX.Element {
        const { renderView, config } = this.props;

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            viewState: {
                isShowAddresList: this.currentOperation === AddressOperation.List,
                isShowAddOrUpdateAddress: this.currentOperation !== AddressOperation.List,
            },
            AccountAddressManagement: {
                moduleProps: this.props,
                className: classnames('ms-account-management-address', config.className)
            },
            showAddressList: this._renderListAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress(config.allowCountryChange)
        };

        return renderView(viewProps) as React.ReactElement;
    }
    private _renderSearchAddress = (): React.ReactElement | null => {
        if (this.showAddressSearch && this.isNZ) {
            return (
                <AddressSearch
                    context={this.props.context}
                    manualAddress={this.manualAddress}
                    addressSelectedCallback={this._handleSearchAddressSelection}
                    toggleManualAddressCallback={this._toggleManualAddressInput}
                />
            );
        }
        return null;
    }
    private _renderAddOrUpdateAddress = (allowCountryChange?: boolean): IAddressAddUpdateProps => {
        let addressFormatItems = this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId);
        this.isNZ = this.addUpdateAddress.ThreeLetterISORegionName === this.nzlCountryRegionId;
        this.addressDropdownsData = this.addressFormat.getPrefilledAddressDropdownData(
            this.resources.addressStateDefaultSelectionText,
            this.stateProvinceInfo
        );
        // if (this.isNZ && !this.manualAddress) {
        //     addressFormatItems = addressFormatItems.filter(item => item.name === 'Name' || item.name === 'Phone');
        // }
        return AddressAddUpdate(
            {
                isUpdating: this.isUpdating,
                resources: this.resources,
                addressType: AddressType.Shipping,
                addressFormat: addressFormatItems,
                defaultCountryRegionId: this.countryRegionId,
                defaultAddressType: this.defaultAddressType,
                selectedAddress: this.addUpdateAddress,
                validationError: this.validationError,
                addressActionResponse: this.addressActionResponse,
                dropdownDisplayData: this.addressDropdownsData,
                telemetryContent: this.telemetryContent,
                onInputChange: this._onAddressAddUpdateInputChange,
                onDropdownChange: this._onAddressAddUpdateDropdownChange,
                onSave: this._onAddressAddUpdateSubmit,
                onCancel: this._resetView,
                searchAddress: this._renderSearchAddress,
                allowCountryChange: allowCountryChange
            }
        );
    }

    private _renderListAddress = (): IAddressListProps => {
        const { config } = this.props;

        return AddressList(
            {
                isUpdating: this.isUpdating,
                selectedAddress: this.selectedAddress,
                addressFormat: this.addressFormat,
                addresses: this.customerAddresses,
                addressPurposes: this.addressPurposes,
                resources: this.resources,
                config:this.config,
                heading: config.heading,
                primaryAddressSectionHeading: config.primaryAddressSectionHeading,
                otherAddressSectionHeading: config.otherAddressSectionHeading,
                contextRequest: this.props.context.request,
                telemetryContent: this.telemetryContent,
                onEditAddress: this._goToEditAddress,
                onRemoveAddress: this._onRemoveAddress,
                onAddAddress: this._goToAddAddress,
                onUpdatePrimaryAddress: this._onSubmitUpdatePrimaryAddress
            }
        );
    };

    private _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#shipping_addressstreet', '#shipping_addressstreet_container', this._onSuggestionSelected);
        }
    }

    @action
    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        set(this.addUpdateAddress, { ['Street']: '' });
        set(this.addUpdateAddress, { ['ZipCode']: address.ZipCode });
        set(this.addUpdateAddress, { ['CountyName']: address.CountyName });
        set(this.addUpdateAddress, { ['City']: address.City });
        set(this.addUpdateAddress, { ['State']: address.State });
        set(this.addUpdateAddress, { ['DistrictName']: address.DistrictName });
        set(this.addUpdateAddress, { ['FullAddress']: address.FullAddress });
        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { ['Street']: address.Street });
        },         0);

    }

    @action
    private _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId);
        addressFormatItem.forEach(format => {
            if (this.addUpdateAddress[format.name] !== undefined && !this.autoSuggest?.excludedAddressFields.includes(format.name)) {
                this.addressFormat[format.name] = '';
            }
        });
        this._clearValidation();
    }

    @action
    private _clearValidation = (): void => {
        this.validationError = {};
    }

    private onAddressAddUpdate = (name: string, value: string | boolean | number) => {
        set(this.addUpdateAddress, { [name]: value });
        this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, name);
    }

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.type === 'checkbox') {
            this.onAddressAddUpdate(event.target.name, event.target.checked);
        } else {
            const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
            this.onAddressAddUpdate(event.target.name, value);
        }
    }

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        if (event.target.name === AddressItemType[AddressItemType.AddressTypeValue]) {
            this.onAddressAddUpdate(event.target.name, +event.target.value);
        } else {
            this.onAddressAddUpdate(event.target.name, event.target.value);
        }
        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    }

    private _onAddressUpdate = (response: Promise<IAddressResponse>, onSuccess: (response: IAddressResponse) => void): void => {

        this.isUpdating = true;
        response.then(
            (result: IAddressResponse) => {
                if (result.address) {
                    this.hasError = false;
                    onSuccess(result);
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                }
                this.isUpdating = false;
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
            }
        );
    }

    private _onAddressAddUpdateSubmit = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            this.manualAddress = true;
            return;
        }
        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            response = this.currentOperation === AddressOperation.Update ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress) : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this._onAddressUpdate(response, this._onAddOrUpdateSuccess);
    }

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterISORegionName =  this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
        set(this.addUpdateAddress, { ['TwoLetterISORegionName']: twoLetterISORegionName });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterISORegionName);
        this._clearAddressFields();
    }

    private _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    }

    private _getStateProvinces = (): void => {
        if (!this.countryRegionId || !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then(
            (result: StateProvinceInfo[]) => {
                const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);
                // Reset state if selected state not found in the list.
                if (!stateInfo) {
                    set(this.addUpdateAddress, { ['State']: '' });
                }
                this.stateProvinceInfo = result;
            });
    }

    private _setDefaultCountryRegionId(): void {
        const { 
            context: {
                request
            },
            config: {
                channelCountryRegionISOCodeOverride
            }
        } = this.props;

        const market = channelCountryRegionISOCodeOverride 
                        ? channelCountryRegionISOCodeOverride 
                        : request && request.channel && request.channel.ChannelCountryRegionISOCode;

        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _updateCurrentOperation = (operation: AddressOperation) => {
        this.currentOperation = operation;
        if (operation === AddressOperation.Add) {
            this.showAddressSearch = true;
            this.manualAddress = false;
        } else if(operation === AddressOperation.Update) {
            this.showAddressSearch = true;
            this.manualAddress = true;
        }
    }

    private _onSubmitUpdatePrimaryAddress = (address: Address): void => {
        this.selectedAddress = address;
        const response = this.addressCommon.updateCustomerPrimaryAddress(address);
        this._onAddressUpdate(response, this._onUpdatePrimaryAddressSuccess);
    }

    private _goToEditAddress = (address: Address): void => {
        this.addUpdateAddress = { ...address };
        this.countryRegionId = this.addUpdateAddress.ThreeLetterISORegionName || this.nzlCountryRegionId;
        this._updateCurrentOperation(AddressOperation.Update);
    }

    private _onRemoveAddress = (address: Address): void => {
        address.Deactivate = true;
        const response = this.addressCommon.updateCustomerAddress(address);
        this._onAddressUpdate(response, this._onAddOrUpdateSuccess);
    }

    private _goToAddAddress = (): void => {
        this.addUpdateAddress = {};
        this._setDefaultCountryRegionId();
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: this.countryRegionId });
        set(this.addUpdateAddress, { ['AddressTypeValue']: this.defaultAddressType });
        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    }

    private _onAddOrUpdateSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
        this._resetView();
    }

    private _onUpdatePrimaryAddressSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
    }

    private _resetView = (): void => {
        this.selectedAddress = undefined;
        this._updateCurrentOperation(AddressOperation.List);
    }
    private _toggleManualAddressInput = (): void => {
        this.manualAddress = !this.manualAddress;
    }

    /**
     * callback function for receiving address details from address search component
     */
    private _handleSearchAddressSelection = (address: IFormattedAddressDetails): void => {
        // check fields
        if (!(address && address.country && address.city && address.postcode && address.street && address.suburb)) {
            this.addUpdateAddress = {
                Name: this.addUpdateAddress.Name,
                Phone: this.addUpdateAddress.Phone,
                ThreeLetterISORegionName: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
            };
            return;
        }

        // cache name and phone in case of country change
        const name = this.addUpdateAddress.Name;
        const phone = this.addUpdateAddress.Phone;

        // country update - needs 3 letter iso
        const countryCode = this._getCountryThreeLetterCode(address.country);
        if (!countryCode) {
            this.props.telemetry.error('country code undefined for ', address.country);
            return;
        }

        this._elementUpdate('ThreeLetterISORegionName', countryCode);

        // save other address fields
        this._elementUpdate('City', address.city);
        this._elementUpdate('ZipCode', address.postcode);
        this._elementUpdate('Street', address.street);
        this._elementUpdate('StreetNumber', address.street_number ? address.street_number : '');
        this._elementUpdate('BuildingCompliment', address.building_name ? address.building_name : '');
        this._elementUpdate('DistrictName', address.suburb);

        // restore name & phone
        if (name) {
            this._elementUpdate('Name', name);
        }
        if (phone) {
            this._elementUpdate('Phone', phone);
        }
    }

    private _elementUpdate = (name: string, value: string) => {
        set(this.addUpdateAddress, { [name]: value });
    }

    private _getCountryThreeLetterCode = (country: string): string | undefined => {
        const dropdownDisplayData = this.addressDropdownsData.ThreeLetterISORegionName;
        const data = dropdownDisplayData.find((item) => item.value === country);
        return data ? data.key?.toString() : undefined;
    }
}

export default AccountManagementAddress;